import React, {useRef, useLayoutEffect} from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import {parseMatrixData ,compareByQuestionSelectsOrder} from "../../../../utils/ChartsUtils";

// am4core.useTheme(am4themes_animated);

function StackedChartComponent(props) {
    const chartRef = useRef(null);

    function am4themes_bubblyTheme(target) {
        if (target instanceof am4core.ColorSet) {
            target.list = [
                am4core.color("#D2B7F9"),
                am4core.color("#9DE0D8"),
                am4core.color("#FFD166"),
                am4core.color("#FBAC79"),
                am4core.color("#A5D6FA"),
                am4core.color("#9AA5F5"),
                am4core.color("#FCE896"),
                am4core.color("#F2768E"),
                am4core.color("#BCE09D"),
                am4core.color("#C9977D"),
                am4core.color("#C2CBCA"),
            ];
        }
    }

    function updateCurrentChartInfo(chart) {
        let newChartInfo = {
            chartText: props.questionTitle,
            chart: chart
        };
        props.updateChartInfo && props.updateChartInfo(props.chartIndex, newChartInfo);
    }

    function createSeries(chart, legendData, field, i, len) {
        let series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueX = field;
        series.dataFields.categoryY = "choiceLabel";
        series.stacked = true;
        series.name = field;
        series.calculateTotals = true;
        series.dataFields.valueXShow = "totalPercent";
        series.calculatePercent = true;
        series.columns.template.tooltipText = "{name}: [bold]{valueX}[/]";

        let labelBullet = series.bullets.push(new am4charts.LabelBullet());
        labelBullet.locationX = 0.5;
        labelBullet.label.text = "{valueX.totalPercent.formatNumber('#.##')}%";
        labelBullet.label.fill = am4core.color("fff");

        labelBullet.label.adapter.add("textOutput", function(text, target) {
            if(target.dataItem && target.dataItem.valueX === 0) {
                return "";
            }
            return text;
        });

        if (i === 1) {
            series.columns.template.column.cornerRadiusTopLeft = 10;
            series.columns.template.column.cornerRadiusBottomLeft = 10;
        }
        if (i === len - 1) {
            series.columns.template.column.cornerRadiusTopRight = 10;
            series.columns.template.column.cornerRadiusBottomRight = 10;

            let valueLabel = series.bullets.push(new am4charts.LabelBullet());
            valueLabel.locationX = 0;
            valueLabel.label.fontSize = 20;
            valueLabel.label.truncate = false;
            valueLabel.label.hideOversized = false;
            valueLabel.label.horizontalCenter = "left";
            valueLabel.label.dx = 50;
            valueLabel.label.adapter.add("text", function (text, target) {
                let value = legendData[target.dataItem.categories.categoryY];
                if (typeof value === 'undefined') {
                    return "N/A"
                } else {
                    return value;
                }
            });
            valueLabel.adapter.add("x", function(x) {
                // Align RESPONSES with the "valueLabel" LabelBullet
                let label = chartRef.current.plotContainer.children.values.find((elem) => {return elem.id === "percent_responses_label"});
                label.x = x * (chart.xAxes.values[0].max/100);

                return x * (chart.xAxes.values[0].max/100);
            });
        }
    }

    const buildChart = (chart) => {
        let data = parseMatrixData(props.answersData, props.questionSelects)
            .sort(compareByQuestionSelectsOrder(props.questionSelects))
            .reverse();

        chart.width = props.chartConfig.width;
        chart.height = props.chartConfig.height;

        chart.data = data;
        chart.legend = new am4charts.Legend();
        chart.legend.position = "bottom";
        chart.paddingRight = 150;
        chart.paddingLeft = 100;
        chart.maskBullets = false;
        let legendData = [];
        for (let i = 0; i < data.length; i++) {
            let sum = 0;
            let keyNames = Object.keys(data[i]);
            for (let j = 0; j < keyNames.length; j++) {

                if (keyNames[j] !== "choiceLabel")
                    sum = sum + data[i][keyNames[j]]
            }
            legendData[data[i].choiceLabel] = sum;
        }

        let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = "choiceLabel";
        categoryAxis.renderer.grid.template.opacity = 0;
        categoryAxis.renderer.labels.template.wrap = !props.isMobile;
        categoryAxis.renderer.labels.template.textAlign = "end";
        categoryAxis.renderer.labels.template.maxWidth = 250;
        categoryAxis.renderer.minGridDistance = 0;
        let valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.max = 100;
        valueAxis.calculateTotals = true;
        valueAxis.labelsEnabled = false;
        valueAxis.renderer.labels.template.adapter.add("text", function (text, target) {
            return "";
        });

        let invisibleContainer = chart.chartContainer.createChild(am4core.Container);
        invisibleContainer.layout = "static";
        invisibleContainer.toBack();
        invisibleContainer.paddingTop = 50;

        let axisTitle = chart.plotContainer.createChild(am4core.Label);
        axisTitle.id = "text_choices_label";
        axisTitle.text = "VALUE";
        axisTitle.fontWeight = 200;
        axisTitle.dx = -80;
        axisTitle.dy = -50;
        axisTitle.zIndex = "5000";

        let dateTitle = chart.plotContainer.createChild(am4core.Label);
        dateTitle.id = "percent_responses_label";
        dateTitle.text = "RESPONSES";
        dateTitle.fontWeight = 200;
        dateTitle.dy = -50;
        dateTitle.zIndex = "5000";

        let keyNames = Object.keys(chart.data[1]);
        for (let i = 0; i < keyNames.length; i++) {

            if (keyNames[i] !== "choiceLabel")
                createSeries(chart, legendData, keyNames[i], i, keyNames.length)
        }
    };

    useLayoutEffect(() => {
        am4core.useTheme(am4themes_bubblyTheme);
        let x = am4core.create("stacked_barchart_" + props.questionId, am4charts.XYChart);

        buildChart(x);

        chartRef.current = x;
        updateCurrentChartInfo(chartRef.current);
        return () => {
            x.dispose();
        };
    }, []);

    useLayoutEffect(() => {
        chartRef.current.width = props.chartConfig.width;
        chartRef.current.height = props.chartConfig.height;
        chartRef.current.yAxes.values[0].renderer.labels.template.maxWidth = props.chartConfig.marginLeft + 250;
        updateCurrentChartInfo(chartRef.current);
    }, [props.chartConfig]);


    function compare( a, b ) {
        if(props.sortingOptions.option === 'desc') {
            if (a[props.sortingOptions.sortingColumn] < b[props.sortingOptions.sortingColumn]) {
                return -1;
            }
            if (a[props.sortingOptions.sortingColumn] > b[props.sortingOptions.sortingColumn]) {
                return 1;
            }
        }else{
            if (a[props.sortingOptions.sortingColumn] > b[props.sortingOptions.sortingColumn]) {
                return -1;
            }
            if (a[props.sortingOptions.sortingColumn] < b[props.sortingOptions.sortingColumn]) {
                return 1;
            }
        }
        return 0;
    }

    function sortData(){

        if(props.sortingOptions && props.sortingOptions.option === 'revert'){
            chartRef.current.data = parseMatrixData(props.answersData)
                .sort(compareByQuestionSelectsOrder(props.questionSelects))
                .reverse();
            return;
        }

        chartRef.current.data = chartRef.current.data.sort(compare);
    }

    useLayoutEffect( () => {
        if(props.sortingOptions !== null) {
            console.log(props.sortingOptions);
            sortData();
        }
        updateCurrentChartInfo(chartRef.current);
    },[props.sortingOptions]);

    return (
        <div className="centered-chart" id={"stacked_barchart_" + props.questionId} style={{width: props.chartConfig.width, height: props.chartConfig.height}}></div>
    );
}

export default StackedChartComponent;